/**
 * Uncaught errors, kept across restarts (2026-10-05).
 *
 * WHY. The operator still gets gig "freezes" that a restart clears, and
 * cannot say when. The black box (`blackBox.ts`) answers two of the three
 * possible causes: the link dropped (heartbeats continue, bridge lost), or iOS
 * stopped the app (heartbeats stop dead). It is blind to the third: the app
 * alive and connected, but stuck behind an exception - a handler that throws
 * on every press reads exactly like a freeze, and the restart that cures it
 * also wipes the console, the only place the error ever showed. So uncaught
 * errors land here, on disk, and ride along in a diagnostics report.
 *
 * Their OWN key, never the durable transition log: an error thrown in a loop
 * would otherwise evict weeks of gig history under that log's byte cap
 * (DEC-202), destroying the evidence while recording the noise.
 *
 * Deduplicated by message + location: a repeat bumps `count` and `last`
 * instead of adding a line, so a loop throwing sixty times a second is ONE
 * entry with a large count - which is itself the diagnosis. Writes are
 * coalesced to at most one per second for the same reason: a storage write per
 * throw would make a bad loop worse. Every storage touch is wrapped; a
 * recorder that can throw would be a diagnostic that causes incidents.
 */

const KEY = 'opbuttons.blackbox.errors.v1';
/** Distinct errors kept, newest wins. A gig rarely shows more than a handful
 *  of distinct ones; 40 is generous and still tiny. */
const MAX_ENTRIES = 40;
/** Byte bound beside the count bound, as in blackBox.ts: a long message could
 *  otherwise grow the key without limit. ~0.25% of an iPad origin quota. */
const MAX_BYTES = 12_000;
const MAX_MESSAGE = 200;
const MAX_STACK = 400;
const SAVE_DELAY_MS = 1_000;

export interface CapturedError {
  /** First and last time this error was seen, wall clock ms. */
  first: number;
  last: number;
  count: number;
  message: string;
  /** `file:line:col`, or the first stack frame. */
  where: string;
  stack: string;
  /** The build that threw it - errors from an older build are kept, and must
   *  not be read as this build's. */
  build: string;
}

let entries: CapturedError[] = [];
let installed = false;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function load(): CapturedError[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CapturedError[]) : [];
  } catch {
    return [];
  }
}

function save(): void {
  saveTimer = null;
  try {
    while (entries.length > MAX_ENTRIES) entries.shift();
    let json = JSON.stringify(entries);
    while (json.length > MAX_BYTES && entries.length > 1) {
      entries.shift();
      json = JSON.stringify(entries);
    }
    localStorage.setItem(KEY, json);
  } catch { /* storage full or blocked: drop silently, never throw */ }
}

function note(message: string, where: string, stack: string): void {
  const msg = message.slice(0, MAX_MESSAGE);
  const at = where.slice(0, MAX_MESSAGE);
  const now = Date.now();
  const hit = entries.find((e) => e.message === msg && e.where === at);
  if (hit) {
    hit.count += 1;
    hit.last = now;
    // Move to the end so eviction from the front drops the stalest.
    entries = entries.filter((e) => e !== hit).concat(hit);
  } else {
    const fresh: CapturedError = { first: now, last: now, count: 1, message: msg, where: at, stack: stack.slice(0, MAX_STACK), build: __BUILD_SHA__ };
    entries.push(fresh);
    for (const fn of listeners) { try { fn(fresh); } catch { /* a listener must never throw out of here */ } }
  }
  if (saveTimer === null) saveTimer = setTimeout(save, SAVE_DELAY_MS);
}

const listeners = new Set<(e: CapturedError) => void>();
/** Called once per NEW distinct error (never for a repeat), so a loop throwing
 *  sixty times a second asks at most once. Returns an unsubscribe. */
export function onNewError(fn: (e: CapturedError) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function describe(reason: unknown): { message: string; stack: string } {
  if (reason instanceof Error) return { message: `${reason.name}: ${reason.message}`, stack: reason.stack ?? '' };
  try {
    return { message: typeof reason === 'string' ? reason : JSON.stringify(reason), stack: '' };
  } catch {
    return { message: String(reason), stack: '' };
  }
}

function firstFrame(stack: string): string {
  const line = stack.split('\n').find((l) => /:\d+:\d+/.test(l));
  return line ? line.trim() : '';
}

/** Start capturing. Idempotent. */
export function installErrorCapture(): void {
  if (installed) return;
  installed = true;
  entries = load();
  window.addEventListener('error', (ev) => {
    try {
      const { message, stack } = ev.error !== undefined && ev.error !== null
        ? describe(ev.error)
        : { message: ev.message || 'unknown error', stack: '' };
      const where = ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : firstFrame(stack);
      note(message, where, stack);
    } catch { /* never throw from the error handler */ }
  });
  window.addEventListener('unhandledrejection', (ev) => {
    try {
      const { message, stack } = describe(ev.reason);
      note(`unhandled rejection: ${message}`, firstFrame(stack), stack);
    } catch { /* never throw from the error handler */ }
  });
}

/** Everything captured, oldest first, including entries not yet flushed. */
export function readCapturedErrors(): CapturedError[] {
  return installed ? entries.slice() : load();
}
